import { PlusIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui'
import type { Edit, Project } from '@/modules/projects/stores/types'
import { CloseButton } from './CloseButton'
import { Count } from './Count'
import { EditHistory } from './EditHistory'
import { LOADING_FILES, PendingNote } from './PendingNote'

export interface WorkbenchCounts {
  accounts: number
  statements: number
  pages: number
  files: number
}

interface WorkbenchHeaderProps {
  project: Project | null
  filesArriving: boolean
  counts: WorkbenchCounts
  toolbar: ReactNode
  onUndo: (edits: Edit[]) => Promise<void>
  onAddAccount: () => void
}

export const WorkbenchHeader = ({
  project,
  filesArriving,
  counts,
  toolbar,
  onUndo,
  onAddAccount,
}: WorkbenchHeaderProps) => (
  <header className="flex items-center gap-8 border-b bg-white py-3 pr-3 pl-4">
    <div className="min-w-0 max-w-xs shrink-0">
      <h1 className="truncate text-base font-semibold leading-6 tracking-tight text-foreground">
        {project?.name ?? 'Workbench'}
      </h1>
      {filesArriving ? (
        <PendingNote text={LOADING_FILES} />
      ) : (
        <p className="truncate text-xs leading-5">
          <Count value={counts.accounts} one="account" many="accounts" />
          <span className="text-ink-subtle"> · </span>
          <Count value={counts.statements} one="statement" many="statements" />
          <span className="text-ink-subtle"> · </span>
          <Count value={counts.pages} one="page" many="pages" />
          <span className="text-ink-subtle"> · </span>
          <Count value={counts.files} one="file" many="files" />
        </p>
      )}
    </div>

    {toolbar}

    <div className="flex shrink-0 items-center gap-2">
      {project ? <EditHistory slug={project.slug} onUndo={onUndo} /> : null}
      <Button theme="outline" size="sm" data-add-account onClick={onAddAccount}>
        <PlusIcon />
        Add account
      </Button>
      {project && <CloseButton slug={project.slug} />}
    </div>
  </header>
)
